import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import koLocale from 'date-fns/locale/ko';
import { AlertDialog, DialogTitleClose, PaperComponent } from '../dialog';
import MobilePdfPreview from '../common/viewer/MobilePdfPreview';
import { createPreviewObjectUrl } from './SubMatFolderFileActions';
import * as subMatFolderActions from '../../store/subMatFolder';
import * as subMatFolderAlarmEmailActions from '../../store/subMatFolderAlarmEmail';
import * as g04GeneralDocuActions from '../../store/g04GeneralDocu';
import { dateFormat } from '../../utils';
import { SubMatExpiryStatusChip } from './SubMatFolderExpiryStatus';

/**
 * 미리보기와 분리된 만료일 설정 다이얼로그 (PDF + endDate/validYN).
 * 국세/지방세 폴더이고 만료일이 없으면 창을 열 때 한 번 읽어 칸만 채운다. 저장은 따로 한다.
 */
const AUTO_NOTE_FILLED = '서류에서 만료일을 읽어 칸에 넣어 두었습니다. 저장을 눌러야 반영됩니다.';
const AUTO_NOTE_FAILED = '만료일을 읽지 못했습니다. 직접 입력한 뒤 저장하세요.';

let generalDocsPromise = null;
const loadGeneralDocs = () => {
  if (!generalDocsPromise) {
    generalDocsPromise = g04GeneralDocuActions.selectAllDirect().catch((error) => {
      generalDocsPromise = null;
      throw error;
    });
  }
  return generalDocsPromise;
};

export const resolveFolderTaxType = async (folderId) => {
  const seen = new Set();
  let id = folderId;
  let generalId = '';
  while (id && !seen.has(id)) {
    seen.add(id);
    const folder = await subMatFolderActions.selectFolderDirect(id);
    const nextGeneralId = folder?.spec?.g04GeneralDocuId;
    if (nextGeneralId) {
      generalId = nextGeneralId;
      break;
    }
    id = folder?.parentId || '';
  }
  if (!generalId) return '';
  const docs = await loadGeneralDocs();
  const code = (docs || []).find((doc) => doc.id === generalId)?.code || '';
  return code === 'NATIONAL_TAX' || code === 'LOCAL_TAX' ? code : '';
};
const EMPTY_ALARM_SLOTS = [
  { period: 0, type: 'D' },
  { period: 0, type: 'D' },
  { period: 0, type: 'D' },
];

const slotsFromAlarms = (alarms) => {
  const current = Array.isArray(alarms) ? alarms.filter((alarm) => alarm && alarm.period > 0) : [];
  const next = current.slice(0, 3).map((alarm) => ({
    period: alarm.period,
    type: alarm.type || 'D',
  }));
  while (next.length < 3) next.push({ period: 0, type: 'D' });
  return next;
};

const SubMatFolderExpiryDialog = ({
  open,
  file,
  folderId,
  onClose,
  onSaved,
  embedAlarm = false,
  gclientId,
  alarms,
  previewOnly = false,
  startTab = 'date',
}) => {
  const isMobile = useMediaQuery((muiTheme) => muiTheme.breakpoints.down('md'));
  const showAlarm = Boolean(embedAlarm && isMobile);
  const [previewUrl, setPreviewUrl] = useState('');
  const previewUrlRef = useRef('');
  const [endDate, setEndDate] = useState(null);
  const [validYN, setValidYN] = useState(false);
  const [taxType, setTaxType] = useState('');
  const [alertInfo, setAlertInfo] = useState({ open: false, titleAlert: '안내', messageAlert: '' });
  const [busy, setBusy] = useState(false);
  const [extractBusy, setExtractBusy] = useState(false);
  const [error, setError] = useState('');
  const [alarmSlots, setAlarmSlots] = useState(EMPTY_ALARM_SLOTS);
  const [mobileTab, setMobileTab] = useState('date');
  const previewOnlyRef = useRef(previewOnly);
  previewOnlyRef.current = previewOnly;
  const startTabRef = useRef(startTab);
  startTabRef.current = startTab;
  const alarmsRef = useRef(alarms);
  alarmsRef.current = alarms;
  const embedAlarmRef = useRef(embedAlarm);
  embedAlarmRef.current = embedAlarm;

  const fid = folderId || file?.folderId;

  useEffect(() => {
    if (!open || !file?.id || !file?.documentPath) {
      return undefined;
    }
    let cancelled = false;
    const noExpiry = file.validYN === true;
    const hasDate = Boolean(file.endDate);
    setError('');
    setAlertInfo((prev) => ({ ...prev, open: false }));
    setValidYN(noExpiry);
    setEndDate(file.endDate ? new Date(file.endDate) : null);
    setTaxType('');
    setExtractBusy(false);
    setMobileTab(startTabRef.current === 'alarm' ? 'alarm' : 'date');
    if (embedAlarmRef.current) setAlarmSlots(slotsFromAlarms(alarmsRef.current));
    (async () => {
      try {
        const url = await createPreviewObjectUrl(file.documentPath);
        if (cancelled) {
          window.URL.revokeObjectURL(url);
          return;
        }
        if (previewUrlRef.current) {
          window.URL.revokeObjectURL(previewUrlRef.current);
        }
        previewUrlRef.current = url;
        setPreviewUrl(url);
      } catch (e) {
        if (!cancelled) setError(e.message || '미리보기를 열 수 없습니다.');
      }
      let code = '';
      try {
        code = await resolveFolderTaxType(folderId || file.folderId);
      } catch (e) {
        code = '';
      }
      if (cancelled || previewOnlyRef.current) return;
      setTaxType(code);
      if (!code || noExpiry || hasDate) return;
      setExtractBusy(true);
      try {
        const parsed = await subMatFolderActions.extractFileExpiryDirect(folderId || file.folderId, file.id, code);
        if (cancelled) return;
        if (parsed) {
          setEndDate(new Date(parsed));
          setValidYN(false);
          setAlertInfo({ open: true, titleAlert: '안내', messageAlert: AUTO_NOTE_FILLED });
        } else {
          setAlertInfo({ open: true, titleAlert: '안내', messageAlert: AUTO_NOTE_FAILED });
        }
      } catch (e) {
        if (!cancelled) setAlertInfo({ open: true, titleAlert: '안내', messageAlert: AUTO_NOTE_FAILED });
      } finally {
        if (!cancelled) setExtractBusy(false);
      }
    })();
    return () => {
      cancelled = true;
      setAlertInfo((prev) => ({ ...prev, open: false }));
      if (previewUrlRef.current) {
        window.URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = '';
      }
      setPreviewUrl('');
    };
    // 열릴 때·파일 전환 시에만 초기화. embedAlarm 변동으로 탭이 만료일로 되돌아가지 않게 한다.
  }, [open, file?.id, file?.documentPath]);

  useEffect(() => {
    if (!open || !embedAlarm) return;
    setAlarmSlots(slotsFromAlarms(alarmsRef.current));
  }, [open, embedAlarm]);

  const handleValidToggle = (checked) => {
    setValidYN(checked);
    if (checked) setEndDate(null);
  };

  const handleExtract = async () => {
    if (!fid || !file?.id || !taxType) return;
    setExtractBusy(true);
    setError('');
    try {
      const parsed = await subMatFolderActions.extractFileExpiryDirect(fid, file.id, taxType);
      if (parsed) {
        setEndDate(new Date(parsed));
        setValidYN(false);
        setAlertInfo({ open: true, titleAlert: '안내', messageAlert: AUTO_NOTE_FILLED });
      } else {
        setAlertInfo({ open: true, titleAlert: '안내', messageAlert: AUTO_NOTE_FAILED });
      }
    } catch (e) {
      setAlertInfo({ open: true, titleAlert: '안내', messageAlert: AUTO_NOTE_FAILED });
    } finally {
      setExtractBusy(false);
    }
  };

  const handleSave = async () => {
    if (!fid || !file?.id) return;
    if (!validYN && !endDate) {
      setError('만료일을 입력하거나 「만료일 없음」을 선택하세요.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const payloadEnd = validYN
        ? null
        : (endDate instanceof Date
          ? dateFormat(endDate.getDateWithEndHours ? endDate.getDateWithEndHours() : endDate)
          : endDate);
      const saved = await subMatFolderActions.updateFileExpiryDirect(fid, file.id, {
        endDate: payloadEnd,
        validYN: Boolean(validYN),
      });
      if (showAlarm) {
        await subMatFolderAlarmEmailActions.bulkCreateDirect({
          gclientId,
          folderId: fid,
          subMatFolderAlarmEmails: alarmSlots.filter((slot) => slot.period > 0),
        });
      }
      onSaved?.(saved);
      onClose?.();
    } catch (e) {
      setError(e?.serverResponse?.message || e.message || '저장에 실패했습니다.');
    } finally {
      setBusy(false);
    }
  };

  const handleAlarmPeriod = (index, raw) => {
    const value = Number(raw);
    const period = Number.isFinite(value) && value > 0 ? Math.floor(value) : 0;
    setAlarmSlots((prev) => prev.map((slot, i) => (i === index ? { ...slot, period } : slot)));
  };

  const alarmUnit = (type) => {
    if (type === 'W') return '주 전';
    if (type === 'M') return '개월 전';
    return '일 전';
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={(_e, reason) => {
        if (reason !== 'backdropClick' && !busy) onClose?.();
      }}
      fullScreen={isMobile}
      fullWidth={!isMobile}
      maxWidth={isMobile ? false : 'lg'}
      PaperComponent={isMobile ? undefined : PaperComponent}
      PaperProps={isMobile ? { sx: { display: 'flex', flexDirection: 'column' } } : undefined}
      aria-labelledby="draggable-dialog-title"
      // DatePicker 팝업·PDF iframe 과 Dialog 포커스 트랩이 겹치면 첫 클릭이 무시됨
      disableEnforceFocus
      disableRestoreFocus
    >
      <DialogTitleClose
        id="draggable-dialog-title"
        onClose={() => !busy && onClose?.()}
        {...(isMobile ? {
          sx: {
            position: 'relative',
            fontSize: '18px !important',
            fontWeight: '600 !important',
            lineHeight: 1.3,
            padding: '16px 56px 16px 16px !important',
            '& .MuiIconButton-root[aria-label="close"]': {
              marginTop: '0 !important',
              top: '50%',
              transform: 'translateY(-50%)',
            },
          },
        } : {})}
      >
        {previewOnly
          ? `서류 미리보기 — ${file?.fileName || ''}`
          : (isMobile ? (file?.fileName || '만료일 설정') : `만료일 설정 — ${file?.fileName || ''}`)}
      </DialogTitleClose>
      <DialogContent
        dividers
        {...(isMobile ? {
          sx: {
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            p: '12px 0 !important',
          },
        } : {})}
      >
        <Box sx={isMobile
          ? { display: 'flex', flexDirection: 'column', gap: 1.5, flex: 1, minHeight: 0 }
          : { display: 'flex', gap: 2, minHeight: 480, flexWrap: { xs: 'wrap', md: 'nowrap' } }}
        >
          {previewUrl ? (
            isMobile ? (
              <Box sx={{ flex: '1 1 58%', height: '58%', maxHeight: '58%', minHeight: 0, overflow: 'hidden', display: 'flex' }}>
                <MobilePdfPreview src={previewUrl} title="서류 미리보기" minFrameHeight={520} />
              </Box>
            ) : (
              <Box
                component="iframe"
                title="서류 미리보기"
                src={previewUrl}
                sx={{ flex: previewOnly ? 1 : 1.4, minWidth: 280, minHeight: 420, border: 1, borderColor: 'divider', borderRadius: 1 }}
              />
            )
          ) : (
            <Box sx={isMobile
              ? { flex: '1 1 58%', height: '58%', maxHeight: '58%', minHeight: 0, border: 1, borderColor: 'divider', borderRadius: 1 }
              : { flex: previewOnly ? 1 : 1.4, minWidth: 280, minHeight: 420, border: 1, borderColor: 'divider', borderRadius: 1 }}
            >
              <Box sx={{ p: 2 }}>
                <Typography variant="body2" color={error ? 'error' : 'text.secondary'}>
                  {error || '미리보기 준비 중…'}
                </Typography>
              </Box>
            </Box>
          )}
          {!previewOnly && (
          <Box sx={isMobile
            ? {
              flex: '1 1 auto',
              minHeight: 0,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              gap: showAlarm ? 0 : 1.5,
              px: '12px',
              bgcolor: 'background.paper',
            }
            : { flex: 1, minWidth: 260, display: 'flex', flexDirection: 'column', gap: 1.5 }}
          >
            {showAlarm && (
              <Box sx={{ display: 'flex', flexShrink: 0, borderBottom: 1, borderColor: 'divider' }}>
                {[
                  { value: 'date', label: '만료일' },
                  { value: 'alarm', label: '알람' },
                ].map((tab) => (
                  <Box
                    key={tab.value}
                    component="button"
                    type="button"
                    onTouchEnd={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      setMobileTab(tab.value);
                    }}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                    }}
                    onClick={(event) => {
                      event.stopPropagation();
                      setMobileTab(tab.value);
                    }}
                    sx={{
                      flex: 1,
                      minHeight: 44,
                      m: 0,
                      p: 0,
                      border: 0,
                      borderBottom: 2,
                      borderColor: mobileTab === tab.value ? 'primary.main' : 'transparent',
                      bgcolor: 'background.paper',
                      color: mobileTab === tab.value ? 'primary.main' : 'text.secondary',
                      fontWeight: mobileTab === tab.value ? 700 : 500,
                      fontSize: '0.875rem',
                      touchAction: 'manipulation',
                    }}
                  >
                    {tab.label}
                  </Box>
                ))}
              </Box>
            )}
            {( !showAlarm || mobileTab === 'date') && (
            <Box sx={{
              display: 'flex',
              flex: showAlarm ? '1 1 auto' : undefined,
              flexDirection: 'column',
              gap: 1.5,
              overflow: 'auto',
              minHeight: 0,
              pt: showAlarm ? 1.5 : 0,
            }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle2">현재 상태</Typography>
              <SubMatExpiryStatusChip file={{ ...file, endDate, validYN }} />
            </Box>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={koLocale}>
              <DatePicker
                label="서류 만료일"
                value={validYN ? null : endDate}
                onChange={(v) => {
                  setValidYN(false);
                  setEndDate(v);
                }}
                disabled={validYN || busy || extractBusy}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    fullWidth
                    onMouseDown={(e) => {
                      // DataGrid 등 상위 포커스 가로채기 방지
                      e.stopPropagation();
                      params.onMouseDown?.(e);
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      params.onClick?.(e);
                    }}
                  />
                )}
                inputFormat="yyyy-MM-dd"
                OpenPickerButtonProps={{
                  onMouseDown: (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  },
                }}
                PopperProps={{
                  sx: { zIndex: (theme) => theme.zIndex.modal + 2 },
                  onMouseDown: (e) => e.stopPropagation(),
                }}
              />
            </LocalizationProvider>
            <FormControlLabel
              control={(
                <Checkbox
                  checked={validYN}
                  onChange={(e) => handleValidToggle(e.target.checked)}
                  disabled={busy}
                />
              )}
              label="만료일 없음"
            />
            {taxType && (
              <Box>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleExtract}
                  disabled={busy || extractBusy}
                >
                  {extractBusy ? '판독 중…' : '다시 판독'}
                </Button>
              </Box>
            )}
            </Box>
            )}
            {showAlarm && mobileTab === 'alarm' && (
              <Box sx={{
                display: 'flex',
                flex: '1 1 auto',
                flexDirection: 'column',
                gap: 1.5,
                overflow: 'auto',
                minHeight: 0,
                pt: 1.5,
              }}>
                <Typography variant="caption" color="text.secondary">
                  0을 설정하시면 알람이 꺼집니다.
                </Typography>
                <Stack spacing={1}>
                  {alarmSlots.map((slot, index) => (
                    <Stack key={`alarm-slot-${index}`} direction="row" spacing={1} alignItems="center">
                      <TextField
                        size="small"
                        type="number"
                        value={slot.period}
                        disabled={busy}
                        onChange={(e) => handleAlarmPeriod(index, e.target.value)}
                        inputProps={{ min: 0, style: { textAlign: 'center' } }}
                        sx={{ width: 96 }}
                      />
                      <Typography variant="body2">{alarmUnit(slot.type)}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </Box>
            )}
            {error && (
              <Typography variant="body2" color="error">{error}</Typography>
            )}
          </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions {...(isMobile ? {
        sx: {
          '&&': {
            justifyContent: 'stretch',
            gap: '12px',
            padding: '12px 16px calc(12px + env(safe-area-inset-bottom)) !important',
          },
          '& .MuiButton-root': {
            flex: 1,
            height: 44,
            marginLeft: '0 !important',
          },
        },
      } : {})}>
        {previewOnly ? (
          <Button variant="text" onClick={() => onClose?.()}>닫기</Button>
        ) : (
          <>
            <Button variant={isMobile ? 'outlined' : 'text'} onClick={() => onClose?.()} disabled={busy}>취소</Button>
            <Button variant="contained" onClick={handleSave} disabled={busy || extractBusy}>
              저장
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
    <AlertDialog alertInfo={alertInfo} setAlertInfo={setAlertInfo} />
    </>
  );
};

export default SubMatFolderExpiryDialog;
